import { ChildRef } from '../../../../../lib/web/components/child-ref';
import { ChildrenRef } from '../../../../../lib/web/components/children-ref';
import { ClickListener } from '../../../../../lib/web/components/click-listener';
import { Component } from '../../../../../lib/web/components/component';
import { ComponentBase } from '../../../../../lib/web/components/component-base';
import { KeyDownListener } from '../../../../../lib/web/components/key-down-listener';
import { Key } from '../../../../../lib/web/core/key';
import { MouseEnterListener } from '../../../../../lib/web/components/mouse-enter-listener';
import { MouseLeaveListener } from '../../../../../lib/web/components/mouse-leave-listener';
import { DataEventService } from '../../../services/data-event.service';

@Component({
    selector: '.o-slider'
})
export class SliderComponent extends ComponentBase<HTMLElement> {
    private _intervalTimer: NodeJS.Timeout | null = null;
    private _touchX: number = null;
    private _touchY: number = null;
    private _touchDirection: 'next' | 'previous' = null;
    private _isMouseEnter: boolean = false;
    private _isTouchMoving: boolean = false;
    private _defaultInterval: number = 5000;
    
    @ChildrenRef(HTMLDivElement, '.o-slider__slide')
    private _slideElements: HTMLDivElement[] = null;
    @ChildrenRef(HTMLButtonElement, '.o-slider__bullet')
    private _bulletElements: HTMLButtonElement[] = null;
    @ChildRef('.o-slider__item-arrow--start')
    private _startArrowElement: HTMLDivElement = null;
    @ChildRef('.o-slider__item-arrow--end')
    private _endArrowElement: HTMLDivElement = null; 
    
    public constructor(node: HTMLElement, private _dataEventService: DataEventService) {
        super(node);
    }

    public onInit(): void {
        this.slide(0);
        this.startSlideInterval();
        this._bulletElements.forEach(b => b.addEventListener('click', () => this.slide(parseInt(b.dataset.index), true)));
        this._slideElements.forEach((b, index) => {
            b.dataset.index = index.toString();
            b.addEventListener('touchstart', e => this.onTouchStart(e));
            b.addEventListener('touchmove', e => this.onTouchMove(e));
            b.addEventListener('touchend', e => this.onTouchEnd(e));
            b.addEventListener('focusin', () => {
                this.clearInterval();
            });
            b.addEventListener('focusout', (e: FocusEvent) => {
                if (!this._node.contains(e.relatedTarget as Node)) {
                    this.startSlideInterval();
                }
            });
        });
    }

    @ClickListener('.o-slider__slide')
    public onViewContent(event: MouseEvent): void {
        const item: HTMLAnchorElement = event.currentTarget as HTMLAnchorElement;        
        this._dataEventService.viewContent(new URL(item.href).pathname);
    }
    
    @MouseEnterListener()
    public onMouseEnter(): void {
        this._isMouseEnter = true;
        this.setArrows();
    }

    @MouseLeaveListener()
    public onMouseOut(): void {
        this._isMouseEnter = false;          
        this._endArrowElement.style.display = 'none';      
        this._startArrowElement.style.display = 'none';
    }

    @ClickListener('.o-slider__item-arrow--start')
    public onStartArrowClick(): void {
        this.slide('previous', true);
    }

    @ClickListener('.o-slider__item-arrow--end')
    public onEndArrowClick(): void {
        this.slide('next', true);
    }

    @KeyDownListener(Key.ArrowLeft, '.o-slider__slide')
    public onSlideArrowLeft(e: KeyboardEvent): void {
        this.handleSlideNavigation(e, 'previous');
    }

    @KeyDownListener(Key.ArrowRight, '.o-slider__slide')
    public onSlideArrowRight(e: KeyboardEvent): void {
        this.handleSlideNavigation(e, 'next');
    }

    private handleSlideNavigation(e: KeyboardEvent, direction: 'next' | 'previous'): void {
        if (document.activeElement !== e.currentTarget) {
            return;
        }
        e.preventDefault();
        this.slide(direction, true);
        setTimeout(() => this._slideElements[this.currentIndex].focus(), 10);
    }
    
    private slide(direction?: 'next' | 'previous' | number, force?: boolean): void {
        const index: number = typeof direction === 'number' ? direction: direction == 'next' ? this.nextIndex: this.previousIndex;
        if (force) {
            this.clearInterval();
        }
        this._slideElements.forEach(s => s.dataset.active = 'false');
        const slide: HTMLDivElement = this._slideElements[index];
        slide.dataset.active = 'true';

        this._slideElements.forEach((s, i) => {
            s.setAttribute('tabindex', i === index ? '0' : '-1');
            s.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        });

        this._bulletElements.forEach(b => b.setAttribute('tabindex', '-1'));

        this._bulletElements.forEach(s => s.dataset.active = 'false');
        const bullet: HTMLButtonElement = this._bulletElements[index];
        bullet.dataset.active = 'true';
        if (this._isMouseEnter)  {
            this.setArrows();
        }
    }

    private onTouchStart(event: TouchEvent): void {
        const touch: Touch = event.touches ? event.touches[0] : null;
        if (touch) {
            this._touchX = touch.clientX;
            this._touchY = touch.clientY;
        }
    }

    private onTouchMove(event: TouchEvent): void {
        const touch: Touch = event.touches ? event.touches[0] : null;
        if (touch) {
            this._isTouchMoving = true;
            const deltaX: number = -1 * (touch.clientX - this._touchX);
            if (deltaX > 0) {
                this._touchDirection = 'next';
            } 
            else {
                this._touchDirection = 'previous';
            }
        }
    }

    private onTouchEnd(event: TouchEvent): void {
        if (this._isTouchMoving) {
            this._isTouchMoving = false;
            if (this._touchDirection == 'next') {
                this.slide('next', true)
            } 
            else if (this._touchDirection == 'previous') {
                this.slide('previous');
            }
        }
    }

    private setArrows(): void {
        if (!document.body.classList.contains('touch')) {
            if (this._slideElements.length > 1) {
                this._startArrowElement.style.display = 'flex';
                this._endArrowElement.style.display = 'flex';
            }
        }
    }

    private clearInterval(): void {
        if (this._intervalTimer) {
            clearInterval(this._intervalTimer);
            this._intervalTimer = null;
        }
    }

    private startSlideInterval(): void {
        this.clearInterval();
        this._intervalTimer = setInterval(() => this.slide('next'), this._defaultInterval);
    }

    public get currentIndex(): number {
        return parseInt(this._slideElements.find(s => s.dataset.active === 'true').dataset.index);
    }

    public get nextIndex(): number {
        const currentIndex: number = this.currentIndex;
        if (currentIndex < this._slideElements.length - 1) {
            return currentIndex + 1;
        }
        else {
            return 0;
        }
    }

    public get previousIndex(): number {
        const currentIndex: number = this.currentIndex;
        if (currentIndex > 0) {
            return currentIndex - 1;
        }
        else {
            return this._slideElements.length - 1;
        }
    }
}